import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    section: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    bayContainer: {
        flex: 1,
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 3,
        breakInside: 'avoid',
        marginHorizontal: 2,
    },
    sectionTitle: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        backgroundColor: '#f0f0f0',
        padding: 3,
        borderRadius: 2,
        textAlign: 'center',
    },
    tempTable: {
        marginBottom: 6,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#e8e8e8',
        padding: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#ccc',
    },
    tableRow: {
        flexDirection: 'row',
        padding: 2,
        borderBottomWidth: 0.5,
        borderBottomStyle: 'solid',
        borderBottomColor: '#eee',
        alignItems: 'center',
    },
    headerCell: {
        flex: 1,
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
        color: '#333',
    },
    tempCell: {
        flex: 1,
        fontSize: 9,
        fontFamily: 'Helvetica',
        textAlign: 'center',
        paddingHorizontal: 2,
    },
    deltaCell: {
        flex: 1,
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
        color: '#2c5aa0',
        paddingHorizontal: 2,
    },
    summarySection: {
        marginTop: 6,
        padding: 4,
        backgroundColor: '#f8f9fa',
        borderRadius: 3,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dee2e6',
    },
    summaryTitle: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
        marginBottom: 4,
        color: '#495057',
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
    },
    summaryItem: {
        flexDirection: 'column',
        alignItems: 'center',
        padding: 3,
    },
    summaryLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#6c757d',
        marginBottom: 2,
    },
    summaryValue: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#2c5aa0',
        paddingHorizontal: 8,
        paddingVertical: 2,
        backgroundColor: '#e9ecef',
        borderRadius: 2,
    },
    conditionsSection: {
        marginTop: 6,
        padding: 3,
    },
    conditionsTitle: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 3,
        color: '#495057',
    },
    conditionsGrid: {
        flexDirection: 'column',
        marginTop: 4,
    },
    conditionRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3,
    },
    conditionItem: {
        width: '48%',
        marginBottom: 3,
    },
    conditionItemThird: {
        width: '32%',
        marginBottom: 3,
        marginRight: 2,
    },
    conditionItemFull: {
        width: '100%',
        marginBottom: 3,
    },
    conditionLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#6c757d',
        marginBottom: 1,
    },
    conditionValue: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#495057',
        lineHeight: 1.2,
    },
    notesItem: {
        width: '100%',
        marginTop: 3,
        paddingTop: 3,
        borderTopWidth: 0.5,
        borderTopStyle: 'solid',
        borderTopColor: '#dee2e6',
    },
    notesValue: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#495057',
        lineHeight: 1.2, // Better line spacing for multiple lines
    },
});
// Treat 0 as invalid so that temperature readings of zero are ignored.
const isValidNumber = (v: any): v is number => typeof v === 'number' && !Number.isNaN(v) && v !== 0;

interface BayData {
    bay: string;
    topFront?: number | null;
    bottomFront?: number | null;
    topMiddle?: number | null;
    bottomMiddle?: number | null;
    topBack?: number | null;
    bottomBack?: number | null;
    avgDeltaTTotal?: number | string | null;
    avgSpDeltaT?: number | string | null;
    topPileConditions?: string | null;
    notes?: string | null;
    ductLiquid?: string | null;
    bugs?: string | null;
    odors?: string | null;
    none?: boolean;
    controlled?: boolean;
    eyesOpen?: boolean;
    peeping?: boolean;
    lessThanHalfIn?: boolean;
    greaterThanHalfIn?: boolean;
    sporadic?: boolean;
    mild?: boolean;
    moderate?: boolean;
    heavy?: boolean;
    hotSpot?: boolean;
    [key: string]: any;
}

const getSproutingConditions = (bay: BayData) => {
    const conditions: string[] = [];
    if (bay.none) conditions.push("None");
    if (bay.controlled) conditions.push("Controlled");
    if (bay.eyesOpen) conditions.push("Eyes Open");
    if (bay.peeping) conditions.push("Peeping");
    if (bay.lessThanHalfIn) conditions.push("< 1/2 in");
    if (bay.greaterThanHalfIn) conditions.push("> 1/2 in");
    return conditions.length > 0 ? conditions.join(', ') : "N/A";
};

const getBreakdownConditions = (bay: BayData) => {
    const conditions: string[] = [];
    if (bay.sporadic) conditions.push("Sporadic");
    if (bay.mild) conditions.push("Mild");
    if (bay.moderate) conditions.push("Moderate");
    if (bay.heavy) conditions.push("Heavy");
    if (bay.hotSpot) conditions.push("Hot Spot");
    return conditions.length > 0 ? conditions.join(', ') : "N/A";
};

const formatConditionLevel = (value: string | null | undefined) => {
    if (value === null || value === undefined) {
        return 'None';
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return 'None';
    }

    const normalized = trimmed.toLowerCase();
    switch (normalized) {
        case 'none':
            return 'None';
        case 'light':
        case 'moderate':
        case 'excessive':
            return normalized.charAt(0).toUpperCase() + normalized.slice(1);
        default:
            return trimmed;
    }
};

type BayProps = {
    bay: BayData;
};

const Bay: React.FC<BayProps> = ({ bay }) => (
    <View style={styles.bayContainer}>
        <Text style={styles.sectionTitle}>Bay: {bay.bay}</Text>

        {/* Temperature Table */}
        <View style={styles.tempTable}>
            {/* Table Header */}
            <View style={styles.tableHeader}>
                <Text style={styles.headerCell}>Position</Text>
                <Text style={styles.headerCell}>Top Temp</Text>
                <Text style={styles.headerCell}>Bottom Temp</Text>
                <Text style={styles.headerCell}>Delta T</Text>
            </View>

            {/* Front Row */}
            {(isValidNumber(bay.topFront) && isValidNumber(bay.bottomFront))
                ? (
                    <View style={styles.tableRow}>
                        <Text style={styles.tempCell}>Front</Text>
                        <Text style={styles.tempCell}>{bay.topFront}°F</Text>
                        <Text style={styles.tempCell}>{bay.bottomFront}°F</Text>
                        <Text style={styles.deltaCell}>{(bay.topFront - bay.bottomFront).toFixed(2)}°F</Text>
                    </View>
                ) : null}

            {/* Middle Row */}
            {(isValidNumber(bay.topMiddle) && isValidNumber(bay.bottomMiddle))
                ? (
                    <View style={styles.tableRow}>
                        <Text style={styles.tempCell}>Middle</Text>
                        <Text style={styles.tempCell}>{bay.topMiddle}°F</Text>
                        <Text style={styles.tempCell}>{bay.bottomMiddle}°F</Text>
                        <Text style={styles.deltaCell}>{(bay.topMiddle - bay.bottomMiddle).toFixed(2)}°F</Text>
                    </View>
                ) : null}

            {/* Back Row */}
            {(isValidNumber(bay.topBack) && isValidNumber(bay.bottomBack))
                ? (
                    <View style={styles.tableRow}>
                        <Text style={styles.tempCell}>Back</Text>
                        <Text style={styles.tempCell}>{bay.topBack}°F</Text>
                        <Text style={styles.tempCell}>{bay.bottomBack}°F</Text>
                        <Text style={styles.deltaCell}>{(bay.topBack - bay.bottomBack).toFixed(2)}°F</Text>
                    </View>
                ) : null}
        </View>

        {/* Summary Calculations */}
        <View style={styles.summarySection}>
            <View style={styles.summaryRow}>
                <View style={styles.summaryItem}>
                    <Text style={styles.summaryLabel}>Avg Delta T</Text>
                    <Text style={styles.summaryValue}>{bay.avgDeltaTTotal}°F</Text>
                </View>
                <View style={styles.summaryItem}>
                    <Text style={styles.summaryLabel}>Avg SP Delta T</Text>
                    <Text style={styles.summaryValue}>{bay.avgSpDeltaT}°F</Text>
                </View>
            </View>
        </View>

        {/* Conditions Section */}
        <View style={styles.conditionsSection}>
            <Text style={styles.conditionsTitle}>Conditions</Text>
            <View style={styles.conditionsGrid}>
                <View style={styles.conditionRow}>
                    {(typeof bay.topPileConditions === 'string' && bay.topPileConditions.trim() !== '') ? (
                        <View style={styles.conditionItemThird}>
                            <Text style={styles.conditionLabel}>Top Pile:</Text>
                            <Text style={styles.conditionValue}>{bay.topPileConditions}</Text>
                        </View>
                    ) : null}
                    <View style={styles.conditionItemThird}>
                        <Text style={styles.conditionLabel}>Sprouting:</Text>
                        <Text style={styles.conditionValue}>{getSproutingConditions(bay)}</Text>
                    </View>
                    <View style={styles.conditionItemThird}>
                        <Text style={styles.conditionLabel}>Breakdown:</Text>
                        <Text style={styles.conditionValue}>{getBreakdownConditions(bay)}</Text>
                    </View>
                </View>
                <View style={styles.conditionRow}>
                    <View style={styles.conditionItemThird}>
                        <Text style={styles.conditionLabel}>Duct Liquid:</Text>
                        <Text style={styles.conditionValue}>{formatConditionLevel(bay.ductLiquid)}</Text>
                    </View>
                    <View style={styles.conditionItemThird}>
                        <Text style={styles.conditionLabel}>Bugs:</Text>
                        <Text style={styles.conditionValue}>{formatConditionLevel(bay.bugs)}</Text>
                    </View>
                    <View style={styles.conditionItemThird}>
                        <Text style={styles.conditionLabel}>Odors:</Text>
                        <Text style={styles.conditionValue}>{formatConditionLevel(bay.odors)}</Text>
                    </View>
                </View>
                {(typeof bay.notes === 'string' && bay.notes.trim() !== '') ? (
                    <View style={styles.notesItem}>
                        <Text style={styles.conditionLabel}>Notes:</Text>
                        <Text style={styles.notesValue}>{bay.notes}</Text>
                    </View>
                ) : null}
            </View>
        </View>
    </View>
);

interface BaySectionProps {
    bay: BayData[];
    inspectionSheet?: unknown;
}

const BaySection: React.FC<BaySectionProps> = ({ bay: bays }) => {
    return (
        <View style={styles.section}>
            {bays.map((bay: BayData) => (
                <Bay key={bay.bay} bay={bay} />
            ))}
        </View>
    );
}

export default BaySection;